import { Languages, MapPin, Sun, Sparkles, Coffee, Heart, Calendar, ShieldCheck, Lightbulb, Flame } from 'lucide-react';

/**
 * Q39 Phase A — ProfileCompatSection
 *
 * Renders three blocks on a user profile when `profile.compat` is present:
 *   1. Quick Snapshot — Looking For + Personality + Group Style + Active times
 *      + Languages + Nationality + Weekend vibe (with sensible fallbacks if a
 *      field is empty so the section degrades gracefully).
 *   2. Conversation Starters — Ask me about, Current obsession, Dream destination.
 *   3. Community Reputation — attendance rate, hosted count, label, host rating.
 *
 * Renders nothing when ALL compat fields and reputation are empty.
 */
const HUMANIZE = {
  looking_for: {
    new_friends: 'New friends',
    fitness_buddies: 'Fitness buddies',
    coffee_chats: 'Coffee chats',
    networking: 'Networking',
    business_connections: 'Business connections',
    travel_companions: 'Travel companions',
    group_activities: 'Group activities',
    language_exchange: 'Language exchange',
  },
  personality: {
    early_bird: 'Early bird', night_owl: 'Night owl', introvert: 'Introvert',
    extrovert: 'Extrovert', adventurous: 'Adventurous', calm: 'Calm',
    competitive: 'Competitive', creative: 'Creative', family_oriented: 'Family-oriented',
    entrepreneur: 'Entrepreneur', foodie: 'Foodie', spiritual: 'Spiritual',
    outdoorsy: 'Outdoorsy', ambitious: 'Ambitious', curious: 'Curious',
  },
  group_style: {
    small_groups: 'Small groups (4–8)',
    large_groups: 'Large groups (20+)',
    deep_conversations: 'Deep conversations',
    active_experiences: 'Active experiences',
    chill_hangouts: 'Chill hangouts',
  },
  active_times: {
    mornings: '☀️ Mornings',
    afternoons: '🌤 Afternoons',
    evenings: '🌇 Evenings',
    late_nights: '🌙 Late nights',
    weekends: '📅 Weekends',
  },
};

function humanize(group, key) {
  return HUMANIZE[group]?.[key] ?? key.replace(/_/g, ' ');
}

export default function ProfileCompatSection({ compat, displayName, isOwn }) {
  if (!compat) return null;
  const {
    looking_for = [], personality = [], group_style = [], active_times = [],
    languages = [], nationality = '', weekend_vibe = '',
    ask_me_about = [], current_obsession = '', dream_destination = '',
    reputation = {}, hosted_categories = [], joined_categories = [],
  } = compat;

  const hasSnapshot = Boolean(looking_for.length || personality.length || group_style.length
    || active_times.length || languages.length || nationality || weekend_vibe
    || hosted_categories.length || joined_categories.length);
  const hasStarters = Boolean(ask_me_about.length || current_obsession || dream_destination);
  const hasRep = Boolean(reputation?.hosted_successfully > 0 || reputation?.attendance_rate);

  if (!hasSnapshot && !hasStarters && !hasRep) return null;

  return (
    <div className="space-y-3 mt-4" data-testid="profile-compat-section">

      {hasSnapshot && (
        <section data-testid="compat-snapshot" className="bg-white rounded-2xl border border-black/[0.06] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#3F5200]" strokeWidth={2.6} />
            <h3 className="text-sm font-extrabold text-[#111]">Quick snapshot</h3>
          </div>

          {looking_for.length > 0 && (
            <Block label="Looking for" testId="snapshot-looking-for">
              {looking_for.map(k => (
                <Chip key={k} testId={`looking-for-${k}`}>{humanize('looking_for', k)}</Chip>
              ))}
            </Block>
          )}

          {personality.length > 0 && (
            <Block label="Personality" testId="snapshot-personality">
              {personality.map(k => (
                <Chip key={k} variant="muted" testId={`personality-${k}`}>{humanize('personality', k)}</Chip>
              ))}
            </Block>
          )}

          {group_style.length > 0 && (
            <Block label="Group style" testId="snapshot-group-style">
              {group_style.map(k => (
                <Chip key={k} variant="muted" testId={`group-style-${k}`}>{humanize('group_style', k)}</Chip>
              ))}
            </Block>
          )}

          {active_times.length > 0 && (
            <Block label="Usually active" testId="snapshot-active-times">
              {active_times.map(k => (
                <Chip key={k} variant="muted" testId={`active-time-${k}`}>{humanize('active_times', k)}</Chip>
              ))}
            </Block>
          )}

          {hosted_categories.length > 0 && (
            <Block label="Usually hosts" testId="snapshot-hosted">
              {hosted_categories.map(c => <Chip key={c} variant="muted">{c}</Chip>)}
            </Block>
          )}
          {joined_categories.length > 0 && (
            <Block label="Usually joins" testId="snapshot-joined">
              {joined_categories.map(c => <Chip key={c} variant="muted">{c}</Chip>)}
            </Block>
          )}

          {(languages.length > 0 || nationality) && (
            <div className="flex flex-wrap items-center gap-3 mt-2 text-[12px] text-[#6B6E73]">
              {languages.length > 0 && (
                <span data-testid="snapshot-languages" className="inline-flex items-center gap-1">
                  <Languages size={13} strokeWidth={2.4} /> {languages.join(', ')}
                </span>
              )}
              {nationality && (
                <span data-testid="snapshot-nationality" className="inline-flex items-center gap-1">
                  <MapPin size={13} strokeWidth={2.4} /> {nationality}
                </span>
              )}
            </div>
          )}

          {weekend_vibe && (
            <p data-testid="snapshot-weekend-vibe" className="mt-3 text-[13px] text-[#111] leading-relaxed bg-[#F1F0EA] rounded-xl p-3">
              <span className="text-[#3F5200] font-bold inline-flex items-center gap-1 mr-1">
                <Sun size={12} strokeWidth={2.6} /> Weekend vibe:
              </span>
              {weekend_vibe}
            </p>
          )}
        </section>
      )}

      {hasStarters && (
        <section data-testid="compat-starters" className="bg-white rounded-2xl border border-black/[0.06] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Coffee size={16} className="text-[#3F5200]" strokeWidth={2.6} />
            <h3 className="text-sm font-extrabold text-[#111]">Conversation starters</h3>
          </div>
          {ask_me_about.length > 0 && (
            <Block label="Ask me about" testId="starters-ask-me-about">
              {ask_me_about.map(t => <Chip key={t}>{t}</Chip>)}
            </Block>
          )}
          {(current_obsession || dream_destination) && (
            <div className="grid grid-cols-1 gap-2 mt-2">
              {current_obsession && (
                <div data-testid="starters-current-obsession" className="bg-[#F1F0EA] rounded-xl p-3">
                  <p className="text-[10px] uppercase font-extrabold text-[#3F5200] tracking-wider mb-0.5 flex items-center gap-1">
                    <Flame size={11} strokeWidth={2.8} /> Current obsession
                  </p>
                  <p className="text-sm font-bold text-[#111]">{current_obsession}</p>
                </div>
              )}
              {dream_destination && (
                <div data-testid="starters-dream-destination" className="bg-[#F1F0EA] rounded-xl p-3">
                  <p className="text-[10px] uppercase font-extrabold text-[#3F5200] tracking-wider mb-0.5 flex items-center gap-1">
                    <Lightbulb size={11} strokeWidth={2.8} /> Dream destination
                  </p>
                  <p className="text-sm font-bold text-[#111]">{dream_destination}</p>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {hasRep && (
        <section data-testid="compat-reputation" className="bg-white rounded-2xl border border-black/[0.06] p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={16} className="text-[#3F5200]" strokeWidth={2.6} />
            <h3 className="text-sm font-extrabold text-[#111]">Community reputation</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Attendance" value={reputation?.attendance_rate != null ? `${reputation.attendance_rate}%` : '—'} testId="rep-attendance" hint={reputation?.attendance_label || ''} />
            <Stat label="Hosted" value={reputation?.hosted_successfully ?? 0} testId="rep-hosted" hint="successful plans" />
            <Stat
              label="Host rating"
              value={reputation?.host_rating ? `${reputation.host_rating} ★` : '—'}
              testId="rep-host-rating"
              hint={reputation?.host_rating_count ? `${reputation.host_rating_count} reviews` : 'No reviews yet'}
            />
            <Stat
              label="Member since"
              value={formatMemberSince(reputation?.member_since_iso)}
              testId="rep-member-since"
              hint=""
            />
          </div>
        </section>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function Block({ label, testId, children }) {
  return (
    <div className="mb-2.5 last:mb-0" data-testid={testId}>
      <p className="text-[10px] font-extrabold text-[#9B9EA3] uppercase tracking-wider mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({ children, variant, testId }) {
  const styles = variant === 'muted'
    ? 'bg-[#F1F0EA] text-[#3F5200] border border-black/[0.04]'
    : 'bg-[#3F5200]/10 text-[#3F5200] border border-[#6B8A00]/30';
  return (
    <span data-testid={testId} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${styles}`}>
      {children}
    </span>
  );
}

function Stat({ label, value, testId, hint }) {
  return (
    <div data-testid={testId} className="bg-[#F1F0EA] rounded-xl p-2.5">
      <p className="text-[9.5px] font-extrabold text-[#9B9EA3] uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-base font-extrabold text-[#111] leading-tight tabular-nums">{value}</p>
      {hint && <p className="text-[10px] text-[#6B6E73] mt-0.5">{hint}</p>}
    </div>
  );
}

function formatMemberSince(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch { return '—'; }
}
